import { isDefined } from "@clipboard-health/util-ts";

import {
  BookabilityDecision,
  type BookabilityStatusItem,
  type BookabilityUnmetCriteria,
  type BookabilityUnmetCriteriaWithMetadata,
} from "./types";

// Conditional type to extract metadata type for specific unmet criteria
type MetadataForUnmetCriteria<T extends BookabilityUnmetCriteria> = Extract<
  BookabilityUnmetCriteriaWithMetadata,
  { unmetCriteria: T }
> extends { metadata: infer M }
  ? M
  : undefined;

/**
 * Returns typed metadata for a given unmet criteria.
 */
export function getBookabilityUnmetCriteriaMetadata<T extends BookabilityUnmetCriteria>(
  bookabilityStatus: BookabilityStatusItem,
  unmetCriteria: T
): MetadataForUnmetCriteria<T> | undefined {
  if (bookabilityStatus.attributes.bookability.decision !== BookabilityDecision.BLOCKED) {
    return undefined;
  }

  if ("unmetCriteriaWithMetadata" in bookabilityStatus.attributes.bookability) {
    const metadata = bookabilityStatus.attributes.bookability.unmetCriteriaWithMetadata.find(
      (unmetCriteriaWithMetadata) => unmetCriteriaWithMetadata.unmetCriteria === unmetCriteria
    );

    if (isDefined(metadata)) {
      // TypeScript will narrow the type based on the discriminated union
      return (
        "metadata" in metadata ? metadata.metadata : undefined
      ) as MetadataForUnmetCriteria<T>;
    }
  }

  return undefined;
}
